import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react';
import type { AccountInfo } from '@azure/msal-browser';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import axios from 'axios';
import api from '../services/api';
import { apiTokenRequest, idTokenRequest } from '../config/authConfig';
import { LOCAL_AUTH_TOKEN_KEY } from '../constants/auth';

export interface UserSession {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive?: boolean;
  agency: {
    id: string;
    name: string;
    statuses?: string;
    transactionTypes?: string;
  };
  /** Present when the user is assigned to a group */
  group?: {
    id: string;
    name: string;
    code: string;
  } | null;
  roles: Array<{
    id: string;
    name: string;
  }>;
}

export interface EmailOtpPendingState {
  challengeId: string;
  maskedEmail: string;
}

interface UserContextType {
  user: UserSession | null;
  loading: boolean;
  error: string | null;
  emailOtpPending: EmailOtpPendingState | null;
  refreshUser: () => Promise<void>;
  syncUser: () => Promise<void>;
  /** Persist local JWT and set session (username/password or post–email OTP for Microsoft) */
  loginWithLocalSession: (accessToken: string, user: UserSession) => void;
  /** Clear local session and sign out of MSAL when applicable */
  logout: () => Promise<void>;
  submitEntraEmailOtp: (code: string) => Promise<void>;
  resendEntraEmailOtp: () => Promise<void>;
  clearEmailOtpFlow: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [emailOtpPending, setEmailOtpPending] = useState<EmailOtpPendingState | null>(null);
  const entraTokenForOtpRef = useRef<string | null>(null);
  const isAuthenticated = useIsAuthenticated();
  const { accounts, instance } = useMsal();

  const acquireMsalTokenForApi = useCallback(
    async (account: AccountInfo) => {
      let tokenResponse;
      try {
        tokenResponse = await instance.acquireTokenSilent({
          ...apiTokenRequest,
          account,
        });
      } catch {
        tokenResponse = await instance.acquireTokenSilent({
          ...idTokenRequest,
          account,
        });
      }
      return tokenResponse.accessToken || tokenResponse.idToken;
    },
    [instance],
  );

  const loadLocalUser = useCallback(async () => {
    const token = localStorage.getItem(LOCAL_AUTH_TOKEN_KEY);
    if (!token) {
      return;
    }
    setEmailOtpPending(null);
    entraTokenForOtpRef.current = null;
    setError(null);
    try {
      const res = await api.get('/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const userData = res.data;
      if (userData && userData.roles && userData.roles.length > 0) {
        if (userData.isActive === false) {
          setUser(null);
          localStorage.removeItem(LOCAL_AUTH_TOKEN_KEY);
          setError('The user is not Active');
          return;
        }
        setUser(userData);
      } else {
        setUser(null);
        localStorage.removeItem(LOCAL_AUTH_TOKEN_KEY);
        setError('The user is not registered in this platform, please contact the Agency Administrator');
      }
    } catch (err: unknown) {
      console.error('loadLocalUser failed:', err);
      localStorage.removeItem(LOCAL_AUTH_TOKEN_KEY);
      setUser(null);
      setError('Session expired. Please sign in again.');
    }
  }, []);

  const handleAuthError = async (errorMessage: string) => {
    console.error('Authentication error:', errorMessage);
    setError(errorMessage);
    setUser(null);
    setEmailOtpPending(null);
    entraTokenForOtpRef.current = null;

    if (localStorage.getItem(LOCAL_AUTH_TOKEN_KEY)) {
      localStorage.removeItem(LOCAL_AUTH_TOKEN_KEY);
      window.location.href = `/login?error=${encodeURIComponent(errorMessage)}`;
      return;
    }

    try {
      const allAccounts = instance.getAllAccounts();
      if (allAccounts.length > 0) {
        await instance.logoutRedirect({
          account: allAccounts[0],
          postLogoutRedirectUri: `${window.location.origin}/login?error=${encodeURIComponent(errorMessage)}`,
        });
      } else {
        window.location.href = `/login?error=${encodeURIComponent(errorMessage)}`;
      }
    } catch (logoutError) {
      console.error('Error during logout:', logoutError);
      window.location.href = `/login?error=${encodeURIComponent(errorMessage)}`;
    }
  };

  const loginWithLocalSession = useCallback((accessToken: string, userSession: UserSession) => {
    localStorage.setItem(LOCAL_AUTH_TOKEN_KEY, accessToken);
    setUser(userSession);
    setError(null);
    setLoading(false);
    setEmailOtpPending(null);
    entraTokenForOtpRef.current = null;
  }, []);

  const submitEntraEmailOtp = useCallback(
    async (code: string) => {
      const pending = emailOtpPending;
      const bearer = entraTokenForOtpRef.current;
      if (!pending || !bearer) {
        throw new Error('Verification session expired. Please sign in again.');
      }
      try {
        const res = await api.post<{ success: boolean; accessToken: string; user: UserSession }>(
          '/auth/email-otp/verify',
          { challengeId: pending.challengeId, code },
          { headers: { Authorization: `Bearer ${bearer}` } },
        );
        if (!res.data.success || !res.data.accessToken || !res.data.user) {
          throw new Error('Unexpected response from server');
        }
        loginWithLocalSession(res.data.accessToken, res.data.user);
      } catch (e: unknown) {
        if (axios.isAxiosError(e)) {
          const d = e.response?.data as { message?: string; error?: string } | undefined;
          throw new Error(d?.message || d?.error || 'Verification failed');
        }
        throw e;
      }
    },
    [emailOtpPending, loginWithLocalSession],
  );

  const resendEntraEmailOtp = useCallback(async () => {
    const bearer = entraTokenForOtpRef.current;
    if (!bearer) {
      throw new Error('Session expired. Please sign in again.');
    }
    const res = await api.post<{ success: boolean; challengeId: string; maskedEmail: string }>(
      '/auth/email-otp/request',
      {},
      { headers: { Authorization: `Bearer ${bearer}` } },
    );
    if (!res.data.success || !res.data.challengeId) {
      throw new Error('Could not resend code');
    }
    setEmailOtpPending({
      challengeId: res.data.challengeId,
      maskedEmail: res.data.maskedEmail,
    });
  }, []);

  const clearEmailOtpFlow = useCallback(async () => {
    setEmailOtpPending(null);
    entraTokenForOtpRef.current = null;
    setUser(null);
    setError(null);
    try {
      const allAccounts = instance.getAllAccounts();
      if (allAccounts.length > 0) {
        await instance.logoutRedirect({
          account: allAccounts[0],
          postLogoutRedirectUri: `${window.location.origin}/login`,
        });
      } else {
        window.location.href = '/login';
      }
    } catch (e) {
      console.error('clearEmailOtpFlow logout:', e);
      window.location.href = '/login';
    }
  }, [instance]);

  const syncUser = async () => {
    if (localStorage.getItem(LOCAL_AUTH_TOKEN_KEY)) {
      setEmailOtpPending(null);
      entraTokenForOtpRef.current = null;
      await loadLocalUser();
      return;
    }

    if (!isAuthenticated || !accounts.length) {
      setUser(null);
      setEmailOtpPending(null);
      entraTokenForOtpRef.current = null;
      return;
    }

    setLoading(true);
    setError(null);
    setEmailOtpPending(null);
    entraTokenForOtpRef.current = null;

    try {
      const account = accounts[0];
      const tokenToUse = await acquireMsalTokenForApi(account);

      const syncResponse = await api.post(
        '/auth/sync-user',
        {
          sub: account.localAccountId,
          email: account.username,
          given_name: account.idTokenClaims?.given_name,
          family_name: account.idTokenClaims?.family_name,
          name: account.name,
        },
        {
          headers: {
            Authorization: `Bearer ${tokenToUse}`,
          },
        },
      );

      if (syncResponse.data.success && syncResponse.data.emailOtpRequired) {
        const otpRes = await api.post<{ success: boolean; challengeId: string; maskedEmail: string }>(
          '/auth/email-otp/request',
          {},
          { headers: { Authorization: `Bearer ${tokenToUse}` } },
        );
        if (!otpRes.data.success || !otpRes.data.challengeId) {
          throw new Error('Could not start email verification');
        }
        entraTokenForOtpRef.current = tokenToUse;
        setEmailOtpPending({
          challengeId: otpRes.data.challengeId,
          maskedEmail: otpRes.data.maskedEmail,
        });
        setUser(null);
        return;
      }

      if (syncResponse.data.success) {
        const userData = syncResponse.data.user;
        if (userData && userData.roles && userData.roles.length > 0) {
          if (userData.isActive === false) {
            setUser(null);
            await handleAuthError('The user is not Active');
            return;
          }
          setUser(userData);
        } else {
          setUser(null);
          await handleAuthError('The user is not registered in this platform, please contact the Agency Administrator');
          return;
        }
      } else {
        const errorCode = syncResponse.data.error;
        const errorMessage =
          syncResponse.data.message || syncResponse.data.error || 'Failed to sync user';

        if (errorCode === 'USER_NOT_REGISTERED' || errorCode === 'USER_NOT_ACTIVE') {
          setUser(null);
          await handleAuthError(errorMessage);
          return;
        }

        throw new Error(errorMessage);
      }
    } catch (err: unknown) {
      console.error('Failed to sync user:', err);
      const errAny = err as { response?: { status?: number; data?: { error?: string; message?: string } } };
      if (errAny.response?.status === 403) {
        const errorData = errAny.response?.data;
        const errorCode = errorData?.error;
        const errorMessage = errorData?.message || 'Authentication failed';

        if (errorCode === 'USER_NOT_REGISTERED' || errorCode === 'USER_NOT_ACTIVE') {
          await handleAuthError(errorMessage);
          return;
        }
      }

      if (errAny.response?.status === 404) {
        setError('API endpoint not found. Please verify the backend is deployed and the API base URL is correct.');
      } else {
        setError(errAny.response?.data?.error || (err instanceof Error ? err.message : 'Failed to sync user'));
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUserInfo = async () => {
    if (localStorage.getItem(LOCAL_AUTH_TOKEN_KEY)) {
      await loadLocalUser();
      return;
    }

    if (!isAuthenticated || !accounts.length) {
      setUser(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const account = accounts[0];
      const tokenToUse = await acquireMsalTokenForApi(account);

      const response = await api.get('/auth/me', {
        headers: {
          Authorization: `Bearer ${tokenToUse}`,
        },
      });

      const userData = response.data;
      if (userData && userData.roles && userData.roles.length > 0) {
        if (userData.isActive === false) {
          setUser(null);
          await handleAuthError('The user is not Active');
          return;
        }
        setUser(userData);
      } else {
        setUser(null);
        await handleAuthError('The user is not registered in this platform, please contact the Agency Administrator');
        return;
      }
    } catch (err: unknown) {
      console.error('Failed to fetch user info:', err);
      const errAny = err as { response?: { status?: number; data?: { error?: string; message?: string } } };
      if (errAny.response?.status === 403) {
        const errorData = errAny.response?.data;
        const errorCode = errorData?.error;
        const errorMessage = errorData?.message || 'Authentication failed';

        if (errorCode === 'EMAIL_OTP_REQUIRED') {
          try {
            const account = accounts[0];
            const tokenToUse = await acquireMsalTokenForApi(account);
            const otpRes = await api.post<{ success: boolean; challengeId: string; maskedEmail: string }>(
              '/auth/email-otp/request',
              {},
              { headers: { Authorization: `Bearer ${tokenToUse}` } },
            );
            if (otpRes.data.success && otpRes.data.challengeId) {
              entraTokenForOtpRef.current = tokenToUse;
              setEmailOtpPending({
                challengeId: otpRes.data.challengeId,
                maskedEmail: otpRes.data.maskedEmail,
              });
              setUser(null);
              setError(null);
              return;
            }
          } catch (otpErr) {
            console.error('Failed to start email OTP after /auth/me 403:', otpErr);
          }
        }

        if (errorCode === 'USER_NOT_REGISTERED' || errorCode === 'USER_NOT_ACTIVE') {
          await handleAuthError(errorMessage);
          return;
        }
      }

      if (errAny.response?.status === 404) {
        setError('API endpoint not found. Please verify the backend is deployed and the API base URL is correct.');
      } else {
        setError(errAny.response?.data?.message || 'Failed to load user information');
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    if (localStorage.getItem(LOCAL_AUTH_TOKEN_KEY)) {
      setLoading(true);
      try {
        await loadLocalUser();
      } finally {
        setLoading(false);
      }
      return;
    }
    await fetchUserInfo();
  };

  const logout = async () => {
    localStorage.removeItem(LOCAL_AUTH_TOKEN_KEY);
    setUser(null);
    setError(null);
    setEmailOtpPending(null);
    entraTokenForOtpRef.current = null;
    try {
      const allAccounts = instance.getAllAccounts();
      if (allAccounts.length > 0) {
        await instance.logoutPopup({ account: allAccounts[0] });
      }
    } catch (e) {
      console.error('Logout error:', e);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (localStorage.getItem(LOCAL_AUTH_TOKEN_KEY)) {
        setLoading(true);
        try {
          await loadLocalUser();
        } finally {
          if (!cancelled) setLoading(false);
        }
        return;
      }

      if (isAuthenticated && accounts.length) {
        setLoading(true);
        try {
          await syncUser();
          if (
            !cancelled &&
            !localStorage.getItem(LOCAL_AUTH_TOKEN_KEY) &&
            !entraTokenForOtpRef.current &&
            isAuthenticated &&
            accounts.length
          ) {
            await fetchUserInfo();
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      } else {
        setUser(null);
        setError(null);
        setEmailOtpPending(null);
        entraTokenForOtpRef.current = null;
        setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, accounts, loadLocalUser]);

  const value: UserContextType = {
    user,
    loading,
    error,
    emailOtpPending,
    refreshUser,
    syncUser,
    loginWithLocalSession,
    logout,
    submitEntraEmailOtp,
    resendEntraEmailOtp,
    clearEmailOtpFlow,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
